import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  Divider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Profile } from '@par/shared/types/player';
import { fetchPost } from '../../api/common';
import { useNavigate, useParams } from 'react-router-dom';

type PropTypes = {
  onCategoryBanClick: () => void;
  onUpdateProfile: () => void;
  profile: Profile;
};

export const ProfileSettings = ({
  onCategoryBanClick,
  onUpdateProfile,
  profile,
}: PropTypes) => {
  const navigate = useNavigate();
  const { profileId } = useParams();

  useEffect(() => {
    if (!profileId || profileId !== profile.id) {
      navigate('/');
    }
  }, [profileId]);

  const handleClearAfterPenaltySwitch = async (checked: boolean) => {
    const updatedProfile = {
      ...profile,
      settings: { ...profile.settings, clearAfterPenalty: checked },
    };
    await fetchPost('/profile/settings', updatedProfile);
    onUpdateProfile();
  };

  const handleConnectionStats = async (checked: boolean) => {
    const updatedProfile = {
      ...profile,
      settings: { ...profile.settings, connectionStats: checked },
    };
    await fetchPost('/profile/settings', updatedProfile);
    onUpdateProfile();
  };

  const handleFastNextSwitch = async (checked: boolean) => {
    const updatedProfile = {
      ...profile,
      settings: { ...profile.settings, fastNext: checked },
    };
    await fetchPost('/profile/settings', updatedProfile);
    onUpdateProfile();
  };

  const handleEnableCustomsSwitch = async (checked: boolean) => {
    const updatedProfile = {
      ...profile,
      settings: { ...profile.settings, enableCustomQuestionsInSolos: checked },
    };
    await fetchPost('/profile/settings', updatedProfile);
    onUpdateProfile();
  };

  return (
    <Container sx={{ display: 'flex', justifyContent: 'center' }}>
      <Stack
        spacing={2}
        sx={{ display: 'flex', justifyContent: 'center' }}
        width="500px"
      >
        <Typography variant="h5" mb={2} textAlign={'center'}>
          Game Settings
        </Typography>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Clear answer after penalty
              <Tooltip
                placement="top"
                title="Turn this option on if you prefer to have your answer cleared after a penalty"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Switch
              checked={!!profile.settings?.clearAfterPenalty}
              onChange={(e) => handleClearAfterPenaltySwitch(e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Show connection stats
              <Tooltip
                placement="top"
                title="This will display connection status, fps, and ping during the game"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Switch
              checked={!!profile.settings?.connectionStats}
              onChange={(e) => handleConnectionStats(e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Fast skip to next question
              <Tooltip
                placement="top"
                title="Turn this option on to quickly move to the next question after answering or timing out. Only allowed in casual/pro/survival solo modes, haunts, and seeded games."
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Switch
              checked={!!profile.settings?.fastNext}
              onChange={(e) => handleFastNextSwitch(e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="80%">
            <Typography variant="body1">
              Enable custom questions in solo modes
              <Tooltip
                placement="top"
                title="By default, custom questions are prevented from appearing in solo modes because of the variability in style, but you can enable it if you want"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="20%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Switch
              checked={!!profile.settings?.enableCustomQuestionsInSolos}
              onChange={(e) => handleEnableCustomsSwitch(e.target.checked)}
            />
          </Box>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack alignItems="center" direction={'row'} my={1}>
          <Box width="70%">
            <Typography variant="body1">
              Category Bans
              <Tooltip
                placement="top"
                title="You can ban a category for ranked games"
              >
                <InfoIcon sx={{ ml: 1, verticalAlign: 'bottom' }} />
              </Tooltip>
            </Typography>
          </Box>
          <Box width="30%" sx={{ display: 'flex', justifyContent: 'right' }}>
            <Button
              variant="text"
              color="primary"
              sx={{ letterSpacing: 2 }}
              onClick={onCategoryBanClick}
            >
              Open list
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};
